import { useLocalStorageManualReset } from '@proj-airi/stage-shared/composables'
import { useHearingStore } from '@proj-airi/stage-ui/stores/modules/hearing'
import { useProviderConfigStore } from '@proj-airi/stage-ui/stores/providers/config'

import { tutorLlmEndpoint, tutorLlmProviderId } from '../constants/llm-endpoint'
import { speechApiBaseUrl, speechApiKey, transcriptionModel } from '../constants/speech-endpoint'

/** AIRI's built-in OpenAI-compatible audio providers, which the gateway speaks to. */
const speechProviderId = 'openai-compatible-audio-speech'
const transcriptionProviderId = 'openai-compatible-audio-transcription'

/**
 * Marks that the pinned provider has been written into the config store.
 *
 * Persisted so that a student who edits the base URL — pointing at a classroom
 * server, say — does not have their change reverted on the next launch.
 */
const seededKey = 'aituter/default-provider-seeded'

/**
 * Installs the pinned LLM endpoint as a configured provider on first launch.
 *
 * Status is set to `bypassed` rather than `unconfigured`: there is nothing for
 * the student to fill in, and leaving it unconfigured makes AIRI's onboarding
 * demand a provider the app already has.
 *
 * Must run after {@link import('./tutor-llm-provider').registerTutorLlmProvider},
 * because `ensureProvider` throws when the definition is not in the registry.
 *
 * Idempotent.
 */
export function installDefaultProviders(): void {
  const seeded = useLocalStorageManualReset<boolean>(seededKey, false)
  if (seeded.value)
    return

  const configStore = useProviderConfigStore()

  try {
    configStore.ensureProvider(tutorLlmProviderId, tutorLlmProviderId, {
      apiKey: tutorLlmEndpoint.apiKey,
      baseUrl: tutorLlmEndpoint.baseUrl,
    })
    configStore.markProviderAdded(tutorLlmProviderId)
    configStore.setProviderStatus(tutorLlmProviderId, 'bypassed')

    installSpeechProviders(configStore)
    seeded.value = true
  }
  catch (error) {
    // Seeding runs during App.vue's onMounted, ahead of the rest of startup.
    // Nothing here is worth taking the app down for; `seeded` stays false so a
    // fixed build retries on the next launch.
    console.error('[aituter] Failed to seed the pinned LLM provider:', error)
  }
}

/**
 * Points the voice in and the voice out at the speech gateway.
 *
 * Synthesis is selected through the character card, which names
 * `openai-compatible-audio-speech`; this only has to supply that provider's
 * credentials. Recognition is not part of a character, so the hearing module's
 * own selection is set here — leaving it empty means the microphone records and
 * nothing ever transcribes it.
 */
function installSpeechProviders(configStore: ReturnType<typeof useProviderConfigStore>): void {
  const config = { apiKey: speechApiKey, baseUrl: `${speechApiBaseUrl}/v1/` }

  for (const providerId of [speechProviderId, transcriptionProviderId]) {
    configStore.ensureProvider(providerId, providerId, config)
    configStore.markProviderAdded(providerId)
    // NOTICE:
    // Must be `configured`, not `bypassed`. `configuredProviders` is derived as
    // `status === 'configured'`, and a watcher in the speech store resets the
    // selection to `speech-noop` whenever the active provider is missing from
    // that list — so a `bypassed` provider loses the tutor's voice a moment
    // after the character card sets it, with nothing logged.
    // Source/context: packages/stage-ui/src/stores/providers/config.ts
    // `configuredProviders`, and stores/modules/speech.ts's
    // `configuredSpeechProvidersMetadata` watcher.
    // Removal condition: none while that derivation stands.
    configStore.setProviderStatus(providerId, 'configured')
  }

  const hearing = useHearingStore()
  hearing.activeTranscriptionProvider = transcriptionProviderId
  hearing.activeTranscriptionModel = transcriptionModel
}
